import cytoscape, { type Core, type ElementDefinition, type NodeCollection, type NodeSingular } from "cytoscape";
import dagre from "cytoscape-dagre";
import type { Graph, GraphNode, Recipe } from "../shared/graph";

cytoscape.use(dagre);

// Keep in sync with the CSS variables in style.css.
const C = {
  text: "#e5e7eb",
  buildingFill: "#16304d",
  buildingBorder: "#4a90d9",
  resourceFill: "#3a2c0e",
  resourceBorder: "#d4a017",
  edge: "#4b5563",
  selected: "#ff4fd8",
  upstream: "#38bdf8",
  downstream: "#a3e635",
};
const FONT = "12px system-ui, sans-serif";
const LAST_LAYOUT_KEY = "whiskerwood:lastLayout";
const WHEEL_ZOOM_KEY = "whiskerwood:wheelDown"; // "in" | "out"

type Position = { x: number; y: number };
type LayoutSummary = { name: string; updatedAt: string };
type Layout = LayoutSummary & { positions: Record<string, Position>; viewport: { zoom: number; pan: Position } | null };

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const els = {
  cy: $<HTMLDivElement>("cy"),
  name: $<HTMLInputElement>("layout-name"),
  save: $<HTMLButtonElement>("save"),
  list: $<HTMLSelectElement>("layout-list"),
  load: $<HTMLButtonElement>("load"),
  del: $<HTMLButtonElement>("delete"),
  autoLayout: $<HTMLButtonElement>("auto-layout"),
  fit: $<HTMLButtonElement>("fit"),
  search: $<HTMLInputElement>("search"),
  names: $<HTMLDataListElement>("node-names"),
  status: $<HTMLDivElement>("status"),
  info: $<HTMLDivElement>("info"),
  wheelZoom: $<HTMLSelectElement>("wheel-zoom"),
};

function setStatus(msg: string, isError = false) {
  els.status.textContent = msg;
  els.status.classList.toggle("error", isError);
}

const storage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string | null) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      // Storage unavailable (private mode etc.): remembered preferences are optional.
    }
  },
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `${res.status} ${res.statusText}`);
  }
  return (res.status === 204 ? null : await res.json()) as T;
}
const layoutUrl = (name: string) => `/api/layouts/${encodeURIComponent(name)}`;

// ---------------------------------------------------------------------------
// Graph

const measure = (() => {
  const ctx = document.createElement("canvas").getContext("2d")!;
  ctx.font = FONT;
  return (s: string) => ctx.measureText(s).width;
})();

function elementsFor(graph: Graph): ElementDefinition[] {
  return [
    ...graph.nodes.map(
      (n): ElementDefinition => ({
        group: "nodes",
        data: { id: n.id, label: n.label, w: Math.ceil(measure(n.label)) + 18 },
        classes: n.kind,
      }),
    ),
    ...graph.edges.map(
      (e): ElementDefinition => ({
        group: "edges",
        data: { id: e.id, source: e.source, target: e.target },
        classes: e.kind === "catalyst" ? "catalyst" : "production",
        selectable: false,
        grabbable: false,
      }),
    ),
  ];
}

const style: cytoscape.StylesheetJson = [
  {
    selector: "node",
    style: {
      label: "data(label)",
      "font-family": "system-ui, sans-serif",
      "font-size": 12,
      color: C.text,
      "text-valign": "center",
      "text-halign": "center",
      width: "data(w)",
      height: 24,
      "border-width": 1.5,
      "overlay-opacity": 0,
      "transition-property": "opacity",
      "transition-duration": 120,
    },
  },
  { selector: "node.building", style: { shape: "rectangle", "background-color": C.buildingFill, "border-color": C.buildingBorder } },
  {
    selector: "node.resource",
    style: { shape: "round-rectangle", "background-color": C.resourceFill, "border-color": C.resourceBorder },
  },
  {
    selector: "edge",
    style: {
      width: 1.5,
      "line-color": C.edge,
      "target-arrow-color": C.edge,
      "target-arrow-shape": "triangle",
      "arrow-scale": 0.8,
      "curve-style": "bezier",
      events: "no",
      "overlay-opacity": 0,
    },
  },
  // Catalysts are consumed slowly; keep them visibly secondary to the production lines.
  {
    selector: "edge.catalyst",
    style: { width: 1, "line-style": "dashed", "line-dash-pattern": [4, 4], opacity: 0.45, "arrow-scale": 0.6 },
  },

  { selector: ".dim", style: { opacity: 0.15 } },
  { selector: "edge.dim", style: { opacity: 0.06 } },

  { selector: "node.up", style: { "border-color": C.upstream, "border-width": 3 } },
  { selector: "node.down", style: { "border-color": C.downstream, "border-width": 3 } },
  {
    selector: "node.up.down",
    style: { "border-color": C.upstream, "outline-color": C.downstream, "outline-width": 3, "outline-offset": 0 },
  },
  {
    selector: "edge.up",
    style: { "line-color": C.upstream, "target-arrow-color": C.upstream, width: 2.5, opacity: 1, "z-index": 10 },
  },
  {
    selector: "edge.down",
    style: { "line-color": C.downstream, "target-arrow-color": C.downstream, width: 2.5, opacity: 1, "z-index": 10 },
  },
  {
    selector: "edge.internal",
    style: { "line-color": C.selected, "target-arrow-color": C.selected, width: 2.5, opacity: 1, "z-index": 10 },
  },
  { selector: "edge.catalyst.up, edge.catalyst.down, edge.catalyst.internal", style: { width: 1.5, opacity: 0.85 } },

  {
    selector: "node:selected",
    style: { "border-color": C.selected, "border-width": 4, "font-weight": "bold", "z-index": 20 },
  },
];

function runAutoLayout(cy: Core) {
  cy.layout({
    name: "dagre",
    rankDir: "LR",
    nodeSep: 10,
    rankSep: 140,
    edgeSep: 6,
    fit: true,
    padding: 40,
    animate: false,
  } as cytoscape.LayoutOptions).run();
}

// ---------------------------------------------------------------------------
// Highlighting and the info panel

function updateHighlight(cy: Core) {
  const sel = cy.nodes(":selected");
  cy.batch(() => {
    cy.elements().removeClass("up down internal dim");
    if (sel.empty()) return;
    sel.incomers("edge").forEach((e) => {
      if (e.source().selected()) e.addClass("internal");
      else e.add(e.source()).addClass("up");
    });
    sel.outgoers("edge").forEach((e) => {
      if (e.target().selected()) e.addClass("internal");
      else e.add(e.target()).addClass("down");
    });
    cy.elements().not(sel).not(".up, .down, .internal").addClass("dim");
  });
}

function renderInfo(cy: Core, nodes: Map<string, GraphNode>) {
  const sel = cy.nodes(":selected");
  if (sel.length !== 1) {
    els.info.hidden = true;
    return;
  }
  const ele = sel[0]!;
  const node = nodes.get(ele.id())!;
  const label = (id: string) => nodes.get(id)?.label ?? id;
  const link = (id: string) => `<a href="#" data-node="${esc(id)}">${esc(label(id))}</a>`;
  const list = (title: string, c: NodeCollection) =>
    c.length
      ? `<h3>${title}</h3><ul>${c
          .map((n) => `<li>${link(n.id())}</li>`)
          .sort()
          .join("")}</ul>`
      : "";
  const byKind = (dir: "in" | "out", kind: "production" | "catalyst") =>
    dir === "in" ? ele.incomers(`edge.${kind}`).sources() : ele.outgoers(`edge.${kind}`).targets();

  let html = `<h2>${esc(node.label)}</h2><div class="kind">${node.kind === "building" ? "Building" : "Resource"}${
    node.wiki ? ` · <a href="${esc(node.wiki)}" target="_blank" rel="noopener">wiki</a>` : ""
  }</div>`;
  if (node.kind === "building") {
    html += list("Consumes", byKind("in", "production"));
    html += list("Catalyst", byKind("in", "catalyst"));
    html += list("Produces", byKind("out", "production"));
    if (node.recipes?.length) {
      const items = (xs: Recipe["inputs"]) =>
        xs.map((x) => `${x.amount ?? "?"} ${link(x.id)}`).join(" + ") || "<i>labor</i>";
      html += `<h3>Recipes</h3>${node.recipes
        .map(
          (r) =>
            `<div class="recipe">${items(r.inputs)} → ${items(r.outputs) || "<i>other</i>"}${
              r.seconds ? ` <span class="time">(${r.seconds}s)</span>` : ""
            }</div>`,
        )
        .join("")}`;
    }
  } else {
    html += list("Produced by", byKind("in", "production"));
    html += list("Consumed by", byKind("out", "production"));
    html += list("Catalyst for", byKind("out", "catalyst"));
  }
  els.info.innerHTML = html;
  els.info.hidden = false;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function focusNode(cy: Core, node: NodeSingular) {
  cy.nodes(":selected").unselect();
  node.select();
  cy.animate({ center: { eles: node }, duration: 250 });
}

// ---------------------------------------------------------------------------
// Panning: the left button is used for box selection, so pan with the right/middle
// button, or with the left button while Space is held.
//
// Cytoscape only handles wheel zoom while user panning is enabled, so its zooming is turned
// off and the wheel is handled here instead.

function installPanning(cy: Core) {
  const container = els.cy;
  let last: Position | null = null;

  // Which way the wheel zooms is a per-browser preference.
  els.wheelZoom.value = storage.get(WHEEL_ZOOM_KEY) === "out" ? "out" : "in";
  els.wheelZoom.addEventListener("change", () => storage.set(WHEEL_ZOOM_KEY, els.wheelZoom.value));

  container.addEventListener("contextmenu", (e) => e.preventDefault());
  container.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();
      // Normalize line/page deltas to pixels; one ~100px wheel notch zooms by ~1.26x.
      const sign = els.wheelZoom.value === "out" ? -1 : 1; // wheel down = positive deltaY
      const px = sign * e.deltaY * (e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? container.clientHeight : 1);
      const rect = container.getBoundingClientRect();
      cy.zoom({
        level: cy.zoom() * Math.pow(10, px / 1000),
        renderedPosition: { x: e.clientX - rect.left, y: e.clientY - rect.top },
      });
    },
    { passive: false },
  );
  container.addEventListener(
    "mousedown",
    (e) => {
      if (e.button !== 1 && e.button !== 2) return;
      last = { x: e.clientX, y: e.clientY };
      container.style.cursor = "grabbing";
      e.preventDefault();
    },
    true,
  );
  window.addEventListener("mousemove", (e) => {
    if (!last) return;
    cy.panBy({ x: e.clientX - last.x, y: e.clientY - last.y });
    last = { x: e.clientX, y: e.clientY };
  });
  window.addEventListener("mouseup", () => {
    if (!last) return;
    last = null;
    container.style.cursor = "";
  });

  const isTyping = () => document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLSelectElement;
  window.addEventListener("keydown", (e) => {
    if (e.code !== "Space" || e.repeat || isTyping()) return;
    e.preventDefault();
    cy.userPanningEnabled(true);
    cy.boxSelectionEnabled(false);
    container.style.cursor = "grab";
  });
  window.addEventListener("keyup", (e) => {
    if (e.code !== "Space") return;
    cy.userPanningEnabled(false);
    cy.boxSelectionEnabled(true);
    container.style.cursor = "";
  });
}

// ---------------------------------------------------------------------------
// Saved layouts

/** A button that needs a second click within a few seconds to act. */
function confirmClick(button: HTMLButtonElement, prompt: string, action: () => void) {
  const original = button.textContent;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const reset = () => {
    clearTimeout(timer);
    timer = undefined;
    button.textContent = original;
    button.classList.remove("confirm");
  };
  button.addEventListener("click", () => {
    if (timer) {
      reset();
      action();
      return;
    }
    button.textContent = prompt;
    button.classList.add("confirm");
    timer = setTimeout(reset, 3000);
  });
}

function setupLayouts(cy: Core) {
  let saved: LayoutSummary[] = [];

  const refreshButtons = () => {
    const name = els.name.value.trim();
    els.save.disabled = name.length === 0;
    els.save.textContent = saved.some((l) => l.name === name) ? "Overwrite" : "Save";
    els.load.disabled = els.del.disabled = !els.list.value;
  };

  const refreshList = async (select?: string) => {
    saved = await api<LayoutSummary[]>("/api/layouts");
    const current = select ?? els.list.value;
    els.list.replaceChildren(
      new Option(saved.length ? "(saved layouts)" : "(no saved layouts)", ""),
      ...saved.map((l) => new Option(l.name, l.name)),
    );
    els.list.value = saved.some((l) => l.name === current) ? current : "";
    refreshButtons();
  };

  const load = async (name: string) => {
    const layout = await api<Layout>(layoutUrl(name));
    let missing = 0;
    cy.batch(() => {
      cy.nodes().forEach((n) => {
        const p = layout.positions[n.id()];
        if (p) n.position(p);
        else missing++;
      });
    });
    if (layout.viewport) cy.viewport(layout.viewport);
    else cy.fit(undefined, 40);
    els.name.value = name;
    els.list.value = name;
    storage.set(LAST_LAYOUT_KEY, name);
    refreshButtons();
    setStatus(
      `Loaded "${name}"` + (missing ? ` · ${missing} node(s) not in this layout kept their current position` : ""),
    );
  };

  els.save.addEventListener("click", async () => {
    const name = els.name.value.trim();
    if (!name) return;
    const positions: Record<string, Position> = {};
    cy.nodes().forEach((n) => {
      const { x, y } = n.position();
      positions[n.id()] = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
    });
    try {
      await api(layoutUrl(name), {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ positions, viewport: { zoom: cy.zoom(), pan: cy.pan() } }),
      });
      storage.set(LAST_LAYOUT_KEY, name);
      await refreshList(name);
      setStatus(`Saved "${name}"`);
    } catch (e) {
      setStatus(`Save failed: ${(e as Error).message}`, true);
    }
  });

  els.load.addEventListener("click", () => {
    if (els.list.value) load(els.list.value).catch((e) => setStatus(`Load failed: ${e.message}`, true));
  });

  confirmClick(els.del, "Really delete?", async () => {
    const name = els.list.value;
    if (!name) return;
    try {
      await api(layoutUrl(name), { method: "DELETE" });
      if (storage.get(LAST_LAYOUT_KEY) === name) storage.set(LAST_LAYOUT_KEY, null);
      await refreshList("");
      setStatus(`Deleted "${name}"`);
    } catch (e) {
      setStatus(`Delete failed: ${(e as Error).message}`, true);
    }
  });

  els.list.addEventListener("change", () => {
    if (els.list.value) els.name.value = els.list.value;
    refreshButtons();
  });
  els.name.addEventListener("input", refreshButtons);
  els.name.addEventListener("keydown", (e) => {
    if (e.key === "Enter") els.save.click();
  });

  return {
    async init() {
      await refreshList();
      const last = storage.get(LAST_LAYOUT_KEY);
      if (last && saved.some((l) => l.name === last)) await load(last);
    },
  };
}

// ---------------------------------------------------------------------------

async function main() {
  const graph = await api<Graph>("/api/graph");
  const nodes = new Map(graph.nodes.map((n) => [n.id, n]));

  const cy = cytoscape({
    container: els.cy,
    elements: elementsFor(graph),
    style,
    boxSelectionEnabled: true,
    userPanningEnabled: false,
    userZoomingEnabled: false, // see installPanning
    selectionType: "single",
    minZoom: 0.05,
    maxZoom: 4,
  });
  (window as unknown as { __cy: Core }).__cy = cy; // handle for debugging from the console
  runAutoLayout(cy);
  installPanning(cy);

  let pending = false;
  cy.on("select unselect", "node", () => {
    // Box selection fires one event per node; recompute once per frame.
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      updateHighlight(cy);
      renderInfo(cy, nodes);
    });
  });

  els.info.addEventListener("click", (e) => {
    const id = (e.target as HTMLElement).closest<HTMLElement>("[data-node]")?.dataset.node;
    if (!id) return;
    e.preventDefault();
    focusNode(cy, cy.getElementById(id) as NodeSingular);
  });

  els.autoLayout.addEventListener("click", () => {
    runAutoLayout(cy);
    setStatus("Auto layout applied (not saved)");
  });
  els.fit.addEventListener("click", () => cy.fit(undefined, 40));

  els.names.replaceChildren(...graph.nodes.map((n) => new Option(n.label)).sort((a, b) => a.value.localeCompare(b.value)));
  const find = () => {
    const q = els.search.value.trim().toLowerCase();
    if (!q) return;
    const hit = cy.nodes().filter((n) => n.data("label").toLowerCase() === q)[0] ??
      cy.nodes().filter((n) => n.data("label").toLowerCase().includes(q))[0];
    if (hit) focusNode(cy, hit);
    else setStatus(`No node matches "${els.search.value}"`, true);
  };
  els.search.addEventListener("change", find);
  els.search.addEventListener("keydown", (e) => {
    if (e.key === "Enter") find();
  });

  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !(document.activeElement instanceof HTMLInputElement)) cy.nodes(":selected").unselect();
  });

  setStatus(`${graph.nodes.length} nodes, ${graph.edges.length} edges`);
  await setupLayouts(cy).init();
}

main().catch((e) => setStatus(`Failed to start: ${e.message}`, true));
